import React, { useCallback, useEffect } from 'react';
import {
  Card,
  CardContent,
  Stack,
  Button,
  Typography,
  Input,
  IconButton,
  Box,
  Tooltip
} from '@mui/joy';
import InfoIcon from "@mui/icons-material/Info";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { formatTitle, createEmptyListItem } from '../../helpers/policyUtils';
import RenderField from './RenderField';

/**
 * Renders list items with drag-drop reordering and field editing
 */
const RenderListItems = (props) => {
  const {
    currentPolicy,
    currentStructure,
    stepIndex,
    setCurrentPolicy,
    onOpenImageReferenceDrawer,
    isGeneratePolicyLoading,
    generatePolicy,
    isTextCropLoading,
    textCropping,
    disableTest,
    setTestResultOpen,
    selectedIndex,
    setSelectedIndex,
    testPage,
    models = []
  } = props;
  const currentStep = currentStructure?.fields?.[stepIndex];
  const isComplianceType = currentPolicy?.settings?.type?.toLowerCase() === "compliance";
  const isTwoColumnLayout = isComplianceType && currentStep?.key === "documentFieldsSchema";
  const hasSelectedField =
    Array.isArray(currentPolicy.policy?.[currentStructure.fields?.[stepIndex]?.key]) &&
    selectedIndex >= 0 &&
    selectedIndex < currentPolicy.policy[currentStructure.fields[stepIndex].key].length;
  const isFillHeight = isTwoColumnLayout;

  /**
   * Resolves options for a field based on optionsRef, optionsFromApi, or static options
   */
  const getFieldOptions = (field) => {
    if (field?.optionsRef) {
      return currentPolicy.policy?.[field.optionsRef]?.map((i) => i.key) || [];
    }
    if (field?.optionsFromApi === 'models') {
      return models
        .filter(m => m.is_active !== false)
        .map(m => ({ value: m.id, label: m.display_name || m.name }));
    }
    return field?.options || [];
  };

  const handleRemoveItem = (listIndex) => {
    const updatedPolicyItems = currentPolicy.policy[currentStructure.fields[stepIndex].key].filter((_, idx) => idx !== listIndex);

    const updatedPolicy = {
      ...currentPolicy,
      policy: {
        ...currentPolicy.policy,
        [currentStructure.fields[stepIndex].key]: updatedPolicyItems
      }
    };

    setSelectedIndex(0);
    setCurrentPolicy(updatedPolicy);
  };

  const handleAddItem = useCallback(() => {
    const emptyItem = createEmptyListItem(currentStructure.fields[stepIndex].fields);
    const updatedPolicy = {
      ...currentPolicy,
      policy: {
        ...currentPolicy.policy,
        [currentStructure.fields[stepIndex].key]: [...currentPolicy.policy[currentStructure.fields[stepIndex].key], emptyItem],
      },
    };
    setCurrentPolicy(updatedPolicy);
    setSelectedIndex(currentPolicy.policy[currentStructure.fields[stepIndex].key].length);
  }, [currentPolicy, currentStructure.fields, setCurrentPolicy, setSelectedIndex, stepIndex]);

  useEffect(() => {
    if (currentPolicy.policy[currentStructure.fields[stepIndex].key].length === 0) {
      handleAddItem();
    }
  }, [stepIndex, currentPolicy.policy, currentStructure.fields, handleAddItem]);

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    const listKey = currentStructure.fields[stepIndex].key;
    const updatedList = Array.from(currentPolicy.policy[listKey]);

    const [movedItem] = updatedList.splice(sourceIndex, 1);
    updatedList.splice(destinationIndex, 0, movedItem);

    setCurrentPolicy(prev => ({
      ...prev,
      policy: {
        ...prev.policy,
        [listKey]: updatedList
      }
    }));
  };

  return (
    <Stack spacing={1} direction={isTwoColumnLayout ? { xs: "column", md: "row" } : "column"} data-testid="list-items-layout" data-layout={isTwoColumnLayout ? "two-column" : "stacked"} sx={{ maxHeight: isFillHeight ? "100%" : "none", height: isFillHeight ? "100%" : "auto", flex: isFillHeight ? 1 : "initial", minHeight: 0, alignItems: isTwoColumnLayout ? "stretch" : "initial", overflow: "hidden" }}>
      <Card variant="outlined" sx={{ borderRadius: 24, flexGrow: isTwoColumnLayout ? 0 : 1, flexBasis: isTwoColumnLayout ? { md: "38%" } : "auto", display: "flex", flexDirection: "column", minWidth: 0, minHeight: 0, height: isTwoColumnLayout ? { md: "100%" } : "auto", overflow: "hidden" }}>
        <CardContent
          sx={{
            flexGrow: 1,
            overflowY: isFillHeight ? "hidden" : "auto",
            overflowX: "hidden",
            scrollbarWidth: "thin",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          <Stack direction="row" sx={{ justifyContent: "space-between", mb: 1, display: "flex", gap: 1 }}>
            <Button size="sm" onClick={handleAddItem}>
              Add field
            </Button>
            <Typography level="body-sm" sx={{ fontWeight: "bold" }}>
              {currentStructure.fields[stepIndex]?.sectionInfo}
            </Typography>
          </Stack>

          {/* List Items */}
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="droppable-list">
              {(provided) => (
                <Stack spacing={1} data-testid="list-items-scroll" data-fill-height={isFillHeight ? "true" : "false"} sx={isFillHeight ? { flex: 1, minHeight: 0, overflow: 'auto', scrollbarWidth: 'thin' } : { overflow: 'auto', minHeight: '14vh', maxHeight: '17vh', scrollbarWidth: 'thin' }} ref={provided.innerRef} {...provided.droppableProps}>
                  {currentPolicy.policy[currentStructure.fields[stepIndex].key].map((listItem, listIndex) => (
                    <Draggable key={listIndex} draggableId={`item-${listIndex}`} index={listIndex}>
                      {(provided, snapshot) => (
                        <Stack
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          direction="row"
                          spacing={2}
                          alignItems="center"
                          sx={{
                            bgcolor: snapshot.isDragging
                              ? "grey.100"
                              : "background.paper",
                            border: "1px solid",
                            borderColor: snapshot.isDragging
                              ? "primary.main"
                              : "transparent",
                            borderRadius: 1,
                            p: 1,
                          }}
                        >
                          <Box
                            {...provided.dragHandleProps}
                            sx={{
                              minWidth: 100,
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontWeight: "bold",
                              padding: "4px 8px",
                              borderRadius: "4px",
                              backgroundColor: "rgba(0, 0, 0, 0.05)",
                              cursor: "grab",
                            }}
                          >
                            <Typography variant="body2">
                              {formatTitle(listItem.type)}
                            </Typography>
                          </Box>

                          <Input
                            disabled={listIndex !== selectedIndex}
                            readOnly
                            sx={{ flexGrow: 1 }}
                            value={
                              stepIndex === 3
                                ? listItem.fieldKey
                                : listItem.key || "Unnamed Field"
                            }
                          />

                          <IconButton onClick={() => setSelectedIndex(listIndex)}>
                            <EditIcon />
                          </IconButton>

                          {listIndex === selectedIndex && (
                            <IconButton onClick={() => handleRemoveItem(listIndex)}>
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </Stack>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </Stack>
              )}
            </Droppable>
          </DragDropContext>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ borderRadius: 24, overflow: "hidden", flexGrow: 1, flexBasis: isTwoColumnLayout ? { md: "62%" } : "auto", minWidth: 0, minHeight: 0, height: isTwoColumnLayout ? { md: "100%" } : "auto" }}>
        <CardContent
          sx={{
            overflowY: isFillHeight ? "hidden" : "auto",
            overflowX: "hidden",
            scrollbarWidth: "thin",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          <Stack direction="row" sx={{ justifyContent: "space-between", mb: 1 }}>
            {/* Test Button (Step 1 Only) */}
            {isTwoColumnLayout && (
              <Stack direction="row" spacing={1}>
                <Button
                  disabled={!hasSelectedField}
                  onClick={onOpenImageReferenceDrawer}
                >
                  Test Field
                </Button>
                <Tooltip
                  title={
                    testPage && testPage.file?.type?.includes('pdf')
                      ? "Generate Policy is not supported for PDF files yet"
                      : ""
                  }
                >
                  <span>
                    <Button
                      disabled={
                        disableTest ||
                        (testPage && testPage.file?.type?.includes('pdf'))
                      }
                      onClick={() => generatePolicy(selectedIndex)}
                      loading={isGeneratePolicyLoading}
                      loadingPosition="start"
                    >
                      {isGeneratePolicyLoading ? "Generating" : "Generate Policy"}
                    </Button>
                  </span>
                </Tooltip>

                <Button
                  disabled={(disableTest || (!(currentPolicy.policy[currentStructure.fields[stepIndex].key][selectedIndex].crop_field ?? false)))}
                  onClick={() => textCropping(selectedIndex)}
                  loading={isTextCropLoading}
                  loadingPosition="start"
                >
                  {isTextCropLoading ? "Cropping" : "Text Crop"}
                </Button>
                {currentPolicy.policy[currentStructure.fields[stepIndex].key][selectedIndex]
                  ?.testResult && (
                    <Button onClick={() => setTestResultOpen(selectedIndex)}>
                      Results
                    </Button>
                  )}
              </Stack>
            )}
            <Typography level="body-sm" sx={{ fontWeight: "bold" }}>
              {currentStructure.fields[stepIndex]?.editSectionInfo}
            </Typography>
          </Stack>

          {/* Field Inputs */}
          <Stack spacing={1} mb={1} data-testid="field-inputs-scroll" data-fill-height={isFillHeight ? "true" : "false"} sx={isFillHeight ? { flex: 1, minHeight: 0, overflow: "auto", scrollbarWidth: "thin" } : { overflow: "auto", minHeight: "14vh", maxHeight: "19vh", scrollbarWidth: "thin" }}>
            {currentStructure?.fields?.[stepIndex]?.fields?.map((field, fieldIndex) => {
              if (
                field?.depends?.type === "exists" &&
                currentPolicy.policy?.[currentStructure.fields?.[stepIndex]?.key]?.[selectedIndex]?.[field.depends.key] !== field.depends.value
              ) {
                return null;
              }

              const options = getFieldOptions(field);

              if (field?.type === "object") {
                return field?.fields?.map((innerField) => {
                  const innerOptions = getFieldOptions(innerField);
                  return (
                    <Stack spacing={1} key={innerField?.key}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography>{formatTitle(innerField?.key)}</Typography>
                        <Tooltip title={innerField?.info} arrow placement="right">
                          <IconButton size="small">
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                      <RenderField
                        type={innerField?.type ?? "text"}
                        value={
                          currentPolicy.policy?.[currentStructure.fields?.[stepIndex]?.key]?.[selectedIndex]?.[field?.key]?.[innerField?.key] ?? innerField?.default ?? ""
                        }
                        options={innerOptions}
                        onChange={(newValue) => {
                          const updatedPolicy = {
                            ...currentPolicy,
                            policy: {
                              ...currentPolicy.policy,
                              [currentStructure.fields?.[stepIndex]?.key]: currentPolicy.policy?.[currentStructure.fields?.[stepIndex]?.key]?.map((li, idx) =>
                                idx === selectedIndex
                                  ? {
                                    ...li,
                                    [field?.key]: {
                                      ...li?.[field?.key],
                                      [innerField?.key]: newValue,
                                    },
                                  }
                                  : li
                              ),
                            },
                          };
                          setCurrentPolicy(updatedPolicy);
                        }}
                      />
                    </Stack>
                  );
                });
              }

              return (
                <Stack spacing={1} key={field?.key}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography>{formatTitle(field?.key)}</Typography>
                    <Tooltip title={field?.info} arrow placement="right">
                      <IconButton size="small">
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                  <RenderField
                    type={field?.type ?? "text"}
                    field={field}
                    value={
                      currentPolicy.policy?.[currentStructure.fields?.[stepIndex]?.key]?.[selectedIndex]?.[field?.key] ?? field?.default ?? ""
                    }
                    options={options}
                    onChange={(newValue) => {
                      const updatedPolicy = {
                        ...currentPolicy,
                        policy: {
                          ...currentPolicy.policy,
                          [currentStructure.fields?.[stepIndex]?.key]: currentPolicy.policy?.[currentStructure.fields?.[stepIndex]?.key]?.map((li, idx) =>
                            idx === selectedIndex ? { ...li, [field?.key]: newValue } : li
                          ),
                        },
                      };
                      setCurrentPolicy(updatedPolicy);
                    }}
                  />
                </Stack>
              );
            })}
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
};

export default RenderListItems;
